import React from "react"

import { EVENTS, simulationDeepDiveEventBus } from "@/inspection-events"
import type { AudioRegionReviewConfig, MetricRecord } from "@/metrics/runtime"

import { logger } from "@/lib/logger"
import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
  SubvalueByTimestamp,
} from "@/types/RunMetricOutput"

import { VolumePitchMisalignmentConfig } from "../../configuration/components/VolumePitchMisalignmentConfig"
import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { parseResultJson } from "../helpers/parseResultJson"
import { WithAudioFilterMixin } from "../mixins/WithAudioFilterMixin"

const log = logger("VolumePitchMisalignmentMetric")

const CATEGORY = "volume_pitch_misalignment"

// Default severity thresholds— intended to be made user-configurable in the future
const SEVERITY_DROP_BELOW = 1.0 // events with severity below this are hidden
const SEVERITY_DANGER_ABOVE = 3.0 // events at or above this are danger; between drop and danger → warning

interface VolumePitchMisalignmentResultJson {
  duration_seconds?: number
  volume_pitch_misalignment_count?: number
  volume_pitch_misalignment_per_minute?: number
  parameters?: {
    min_volume_change_for_pitch_misalignment?: number
    min_volume_change_for_pitch_alignment?: number
  }
}

export class VolumePitchMisalignmentMetricRecord extends WithAudioFilterMixin(
  NumericalMetricRecord,
) {
  overviewSize = "small" as const

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    try {
      const formatTime = (s: number) =>
        `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`

      const resultJson = parseResultJson<VolumePitchMisalignmentResultJson>(mo)
      // Use top-level subvalues_by_timestamp
      const events: SubvalueByTimestamp[] =
        mo.subvalues_by_timestamp?.length > 0
          ? mo.subvalues_by_timestamp
          : // @deprecated fallback: result_json.subvalues_by_timestamp — remove after subvalues_by_timestamp migration is complete
            Array.isArray(mo.result_json?.subvalues_by_timestamp)
            ? (mo.result_json.subvalues_by_timestamp as SubvalueByTimestamp[])
            : []
      const rawCount =
        (mo.result?.count as number | undefined) ??
        resultJson?.volume_pitch_misalignment_count ??
        events.length
      // float_value is now mean severity, not per-minute; use typed frequency
      // then legacy result_json rate, no float_value fallback.
      const ratePerMinute =
        (mo.result?.frequency as number | undefined) ??
        resultJson?.volume_pitch_misalignment_per_minute ??
        0

      // New format: result_json does NOT contain subvalues_by_timestamp (events live
      // exclusively in the top-level column and carry a per-event severity float_value).
      // Old format: result_json still has the legacy subvalues_by_timestamp array
      const hasNewFormat = !Array.isArray(
        mo.result_json?.subvalues_by_timestamp,
      )

      const droppedCount = hasNewFormat
        ? events.filter(
            (e) => e.float_value == null || e.float_value < SEVERITY_DROP_BELOW,
          ).length
        : 0
      const totalCount = rawCount - droppedCount

      // Severity score stats — only meaningful for new-format data, filtered by threshold
      const scores = hasNewFormat
        ? events
            .map((e) => e.float_value)
            .filter(
              (v): v is number =>
                v != null && Number.isFinite(v) && v >= SEVERITY_DROP_BELOW,
            )
        : []
      const mean =
        scores.length > 0
          ? scores.reduce((a, b) => a + b, 0) / scores.length
          : null
      const std =
        mean != null && scores.length > 1
          ? Math.sqrt(
              scores.reduce((sum, v) => sum + (v - mean) ** 2, 0) /
                scores.length,
            )
          : null
      const p95 = (() => {
        if (scores.length === 0) {
          return null
        }
        const sorted = [...scores].sort((a, b) => a - b)
        const idx = Math.floor(sorted.length * 0.95)
        return sorted[Math.min(idx, sorted.length - 1)]
      })()

      const handleSeekToTimestamp = (timestamp: number) => {
        simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_SEEK, {
          time: timestamp,
        })
      }

      return (
        <div className="space-y-6">
          {scores.length > 0 && (
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 dark:bg-amber-900/20 dark:border-amber-800">
                <div className="text-sm text-amber-600 font-medium mb-1 dark:text-amber-400">
                  Mean Severity
                </div>
                <div className="text-2xl font-bold text-amber-800 dark:text-amber-200">
                  {mean!.toFixed(2)}
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 dark:bg-amber-900/20 dark:border-amber-800">
                <div className="text-sm text-amber-600 font-medium mb-1 dark:text-amber-400">
                  Std Severity
                </div>
                <div className="text-2xl font-bold text-amber-800 dark:text-amber-200">
                  {std != null ? std.toFixed(2) : "—"}
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 dark:bg-amber-900/20 dark:border-amber-800">
                <div className="text-sm text-amber-600 font-medium mb-1 dark:text-amber-400">
                  P95 Severity
                </div>
                <div className="text-2xl font-bold text-amber-800 dark:text-amber-200">
                  {p95 != null ? p95.toFixed(2) : "—"}
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 dark:bg-amber-900/20 dark:border-amber-800">
              <div className="text-sm text-amber-600 font-medium mb-1 dark:text-amber-400">
                Total Events
              </div>
              <div className="text-2xl font-bold text-amber-800 dark:text-amber-200">
                {totalCount}
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 dark:bg-amber-900/20 dark:border-amber-800">
              <div className="text-sm text-amber-600 font-medium mb-1 dark:text-amber-400">
                Rate
              </div>
              <div className="text-2xl font-bold text-amber-800 dark:text-amber-200">
                {ratePerMinute.toFixed(2)}/min
              </div>
            </div>
          </div>

          {events.length > 0 &&
            (() => {
              const BIN_SIZE = 3 // seconds
              const binsMap = new Map<number, typeof events>()
              for (const sv of events) {
                // For new-format data, drop events below the severity threshold
                if (
                  hasNewFormat &&
                  (sv.float_value == null ||
                    sv.float_value < SEVERITY_DROP_BELOW)
                ) {
                  continue
                }
                const bin = Math.floor(sv.start_offset / BIN_SIZE)
                if (!binsMap.has(bin)) {
                  binsMap.set(bin, [])
                }
                binsMap.get(bin)!.push(sv)
              }
              const bins = Array.from(binsMap.entries()).sort(
                ([a], [b]) => a - b,
              )
              if (bins.length === 0) {
                return null
              }
              return (
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
                    Volume/Pitch Misalignment Events
                  </h3>
                  <div className="space-y-3">
                    {bins.map(([binIndex, binEvents]) => {
                      const binStart = binIndex * BIN_SIZE
                      const binEnd = (binIndex + 1) * BIN_SIZE
                      return (
                        <div
                          key={binIndex}
                          className="border border-gray-200 rounded-lg bg-white overflow-hidden dark:border-gray-700 dark:bg-gray-900"
                        >
                          <button
                            type="button"
                            className="w-full text-left flex items-center gap-2 px-4 py-2 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700"
                            onClick={() => handleSeekToTimestamp(binStart)}
                          >
                            <span className="text-sm font-mono text-gray-600 dark:text-gray-400">
                              {formatTime(binStart)} – {formatTime(binEnd)}
                            </span>
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-grey-100 text-grey-800 dark:bg-grey-900/20 dark:text-grey-400">
                              {binEvents.length} event
                              {binEvents.length !== 1 ? "s" : ""}
                            </span>
                          </button>
                          <div className="divide-y divide-gray-100 dark:divide-gray-800">
                            {binEvents.map((sv, i) => {
                              const isDanger =
                                hasNewFormat &&
                                sv.float_value != null &&
                                sv.float_value >= SEVERITY_DANGER_ABOVE
                              const severityClass = isDanger
                                ? "text-red-600 dark:text-red-400"
                                : hasNewFormat && sv.float_value != null
                                  ? "text-yellow-600 dark:text-yellow-400"
                                  : "text-yellow-500 dark:text-yellow-400"
                              const badgeClass = isDanger
                                ? "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800"
                                : "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800"
                              return (
                                <button
                                  type="button"
                                  key={i}
                                  className="w-full text-left flex items-center gap-2 px-4 py-2 hover:bg-gray-50 transition-colors cursor-pointer dark:hover:bg-gray-950"
                                  onClick={() =>
                                    handleSeekToTimestamp(sv.start_offset)
                                  }
                                >
                                  <span
                                    className={`px-2 py-0.5 rounded text-xs font-semibold border ${badgeClass}`}
                                  >
                                    Misalignment
                                  </span>
                                  <span className="text-sm text-gray-600 font-mono dark:text-gray-400">
                                    {formatTime(sv.start_offset)}
                                  </span>
                                  {hasNewFormat && sv.float_value != null && (
                                    <span
                                      className={`ml-auto text-xs font-mono ${severityClass}`}
                                    >
                                      severity: {sv.float_value.toFixed(3)}{" "}
                                    </span>
                                  )}
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })()}

          {totalCount === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No volume/pitch misalignments detected
            </div>
          )}
        </div>
      )
    } catch (error) {
      log.error("Error rendering volume pitch misalignment deep dive", error)
      return <div className="text-red-500">Error displaying metric details</div>
    }
  }

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (mo?.error_message && mo?.error_message !== "") {
      return []
    }
    try {
      // Use top-level subvalues_by_timestamp (preferred source)
      const subvalues: SubvalueByTimestamp[] =
        (mo.subvalues_by_timestamp?.length ?? 0) > 0
          ? (mo.subvalues_by_timestamp ?? [])
          : // @deprecated fallback: result_json.subvalues_by_timestamp — remove after subvalues_by_timestamp migration is complete
            Array.isArray(mo.result_json?.subvalues_by_timestamp)
            ? (mo.result_json.subvalues_by_timestamp as SubvalueByTimestamp[])
            : []
      const hasNewFormat = !Array.isArray(
        mo.result_json?.subvalues_by_timestamp,
      )
      return subvalues.map((item, idx) => {
        const isDanger =
          hasNewFormat &&
          item.float_value != null &&
          item.float_value >= SEVERITY_DANGER_ABOVE
        const color = isDanger
          ? "rgba(239, 68, 68, 0.7)" // red-500
          : "rgba(245, 158, 11, 0.7)" // amber-500
        return {
          id: `${CATEGORY}:${idx}`,
          start: item.start_offset,
          end: Math.max(item.end_offset, item.start_offset + 0.5),
          color,
          drag: false,
          resize: false,
        }
      })
    } catch (e) {
      log.debug(
        "Volume pitch misalignment data unavailable or could not parse",
        { error: e },
      )
      return []
    }
  }

  getAudioRegionReviewConfig(): AudioRegionReviewConfig | null {
    return {
      category: CATEGORY,
      scoreType: "per_minute",
      buildReviewedResultJson: ({
        editedCount,
        score,
        durationSeconds,
        metricOutput,
      }) => {
        const resultJson = metricOutput
          ? parseResultJson<VolumePitchMisalignmentResultJson>(metricOutput)
          : undefined
        const minVolumeChangeForPitchMisalignment =
          resultJson?.parameters?.min_volume_change_for_pitch_misalignment ??
          resultJson?.parameters?.min_volume_change_for_pitch_alignment
        return {
          volume_pitch_misalignment_count: editedCount,
          volume_pitch_misalignment_per_minute: Number(score.toFixed(2)),
          duration_seconds:
            durationSeconds != null ? Number(durationSeconds.toFixed(2)) : null,
          parameters: {
            min_volume_change_for_pitch_misalignment:
              minVolumeChangeForPitchMisalignment,
          },
        }
      },
    }
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
    textAreaRef?: React.RefObject<HTMLTextAreaElement>,
    editorContext?: Parameters<MetricRecord["renderEditorMetadata"]>[3],
  ): React.ReactNode {
    return (
      <div className="flex flex-col gap-4">
        <VolumePitchMisalignmentConfig
          metricRecordData={metricRecordData}
          onChange={onChange}
        />
        {super.renderEditorMetadata(
          metricRecordData,
          onChange,
          textAreaRef,
          editorContext,
        )}
      </div>
    )
  }
}
